import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { Utils } from 'src/app/services/utils';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-accept-terms',
  templateUrl: './accept-terms.component.html',
  styleUrls: ['./accept-terms.component.scss'],
})
export class AcceptTermsComponent implements OnInit {
  scrolledToEnd = false;
  submitting = false;
  error = '';
  redirecting = false;

  /** Public policy pages (health chat / www site). */
  readonly healthChatHomeUrl = `${this.policySiteBase}/`;
  readonly termsOfUseUrl = `${this.policySiteBase}/policies/terms`;
  readonly privacyPolicyUrl = `${this.policySiteBase}/policies/privacy`;
  readonly baaUrl = `${this.policySiteBase}/policies/baa`;

  private get policySiteBase(): string {
    const raw =
      environment.ayaHealthChatAppUrl?.trim() || 'https://www.healthexam.com/';
    return raw.replace(/\/+$/, '');
  }

  constructor(
    private readonly _authService: AuthService,
    private readonly _localStorage: LocalStorageService,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    void this.bootstrapEntry();
  }

  private async bootstrapEntry(): Promise<void> {
    await this.bootstrapSessionFromQuery();

    if (!this._localStorage.authorizationData?.token) {
      void this._router.navigate(['/login']);
      return;
    }
    await this.bootstrap();
  }

  /** Allow Aya apps to reopen accept-terms with an active session in the query string. */
  private async bootstrapSessionFromQuery(): Promise<void> {
    const q = this._route.snapshot.queryParams;
    const token = String(q['token'] || '').trim();
    const email = String(q['email'] || '').trim();
    const refreshToken = String(q['refreshToken'] || '').trim();
    const returnUrl = String(q['returnUrl'] || '').trim();

    if (returnUrl) {
      const sanitized = Utils.sanitizeReturnUrl(returnUrl);
      if (sanitized) {
        this._authService.storePendingAyaReturnUrl(sanitized);
      }
    }

    if (!token || !email) {
      return;
    }

    const authToken = await this._authService.hydrateAuthTokenRefreshToken(
      email,
      {
        accessToken: token,
        refreshToken,
      }
    );
    this._authService.persistAuthData(email, authToken);
  }

  private async bootstrap(): Promise<void> {
    try {
      const me = await firstValueFrom(this._authService.getIdentityMe());
      if (me?.user?.hasUserAcceptedTerms === true) {
        await this.finishHandoff();
      }
    } catch {
      // User can still read and accept on this page.
    }
  }

  onTermsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el) {
      return;
    }
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 32;
    if (atBottom) {
      this.scrolledToEnd = true;
    }
  }

  async agree(): Promise<void> {
    if (!this.scrolledToEnd || this.submitting) {
      return;
    }
    this.error = '';
    this.submitting = true;
    try {
      await firstValueFrom(this._authService.acceptTermsAndConditions());
      this.redirecting = true;
      await this.finishHandoff();
    } catch (err: unknown) {
      const message =
        (err as { error?: { message?: string } })?.error?.message ||
        'Could not save your acceptance. Please try again.';
      this.error = message;
      this.submitting = false;
    }
  }

  private async finishHandoff(): Promise<void> {
    try {
      await this._authService.completePendingAyaHandoffAfterTerms();
    } catch (err: unknown) {
      this.redirecting = false;
      this.submitting = false;
      const message =
        (err as { error?: { message?: string } })?.error?.message ||
        'Could not continue to Aya. Please try again.';
      this.error = message;
    }
  }
}
